import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import { Button } from "@/design-system/Button/Button";
import { CompletedChip } from "@/design-system/CompletedChip/CompletedChip";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import { ProgressBar } from "@/design-system/ProgressBar/ProgressBar";
import { NewTag } from "@/design-system/Tags/NewTag/NewTag";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { safeGetItem } from "@/utils/safeStorage";
import {
  GALLERY_SYNC_SKIPPED_KEY,
  type GalleryStepState,
  galleryImportAge,
  galleryStepStates,
} from "@repo/shared/gallery";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { formatTime } from "@repo/shared/utils";
import { ArrowRight, Check, Clock, RefreshCw } from "lucide-react";
import { Fragment, ReactNode, useRef, useState } from "react";
import { NavLink } from "react-router";
import { useMediaQuery } from "usehooks-ts";

const CARD_BORDER: Record<GalleryStepState, string> = {
  done: "border-neutral-700",
  current: "border-neutral-700",
  optional: "border-dashed border-neutral-600 opacity-75",
  locked: "border-neutral-700 opacity-60",
};

type Step = {
  key: string;
  state: GalleryStepState;
  tag?: ReactNode;
  title: string;
  description: ReactNode;
  footer: ReactNode;
};

/** `stepOf`: the phone stack's "Step n of m", in the card's top right. */
const StepCard = ({ step, stepOf }: { step: Step; stepOf?: string }) => {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 basis-0 flex-col gap-3 rounded-2xl border bg-neutral-800 p-4 transition-[border-color,opacity,box-shadow] duration-200",
        CARD_BORDER[step.state],
      )}
    >
      <div className="flex flex-col gap-1">
        <span className="flex items-start gap-3">
          <span className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-body-lg text-neutral-100">{step.title}</span>
            {step.tag}
          </span>
          {stepOf && (
            <span className="shrink-0 whitespace-nowrap pt-0.5 text-small tabular-nums text-neutral-500">
              {stepOf}
            </span>
          )}
        </span>
        <span className="text-body text-neutral-400">{step.description}</span>
      </div>
      {/* A medium button's height, so a text link centres on the same line as the other cards' buttons. */}
      <div className="mt-auto flex min-h-10 items-center gap-2">
        {step.footer}
      </div>
    </div>
  );
};

const SyncProgress = ({ progress }: { progress: number }) => {
  const { t } = useTranslation("gallery");
  return (
    <div className="flex min-w-32 flex-1 basis-48 items-center gap-2.5">
      <ProgressBar small ratio={progress / 100} className="flex-1" />
      <span className="whitespace-nowrap text-small tabular-nums text-neutral-500">
        {t("sync.syncing", { progress })}
      </span>
    </div>
  );
};

/** What a sync does, and when the next one opens while the cycle's is used up. */
const SyncInfo = ({ nextSyncAt }: { nextSyncAt?: string }) => {
  const { t } = useTranslation("gallery");
  return (
    <InfoTip label={t("steps.sync.title")} width={440}>
      <Trans
        ns="gallery"
        i18nKey="steps.sync.info"
        components={{
          b: <strong />,
          p: <span className="mb-2 block last:mb-0" />,
          guide: (
            <NavLink
              to="/fut-gallery/update-first-owners"
              className="text-blue-400 underline"
            />
          ),
        }}
      />
      {nextSyncAt && (
        <span className="mt-2 block">
          {t("steps.sync.locked", { time: nextSyncAt })}
        </span>
      )}
    </InfoTip>
  );
};

/** Synchronize plus its info tip; disabled while no sync can start. */
const SyncButton = ({
  disabled = false,
  nextSyncAt,
  onOpen,
}: {
  disabled?: boolean;
  nextSyncAt?: string;
  onOpen: () => void;
}) => {
  const { t } = useTranslation("gallery");
  return (
    <span className="flex items-center gap-2">
      <Button
        variant="magic"
        size="medium"
        disabled={disabled || !!nextSyncAt}
        onClick={onOpen}
      >
        <span className="whitespace-nowrap">{t("steps.sync.cta")}</span>
      </Button>
      <SyncInfo nextSyncAt={nextSyncAt} />
    </span>
  );
};

export type GalleryStepsProps = {
  clubImported: boolean;
  /** The last Club Import, in ms. */
  lastImportedAt?: number;
  onImportClub: () => void;
  /** The Build step; without it or `lastStep` the steps end at Sync. */
  build?: {
    /** Every set's solution is up to date and no run is going. */
    done: boolean;
    running: boolean;
    /** The run's solved count, ticking up while it runs. */
    progress: { done: number; total: number };
    disabled: boolean;
    /** At least one set has a solution: the first build happened. */
    built: boolean;
    /** A Credit is spent: the user is signed in. */
    costsCredit: boolean;
    /** Players collected since the oldest solution, shown beside Update Solutions. */
    newCollectedCount: number;
    onBuild: () => void;
  };
  /** A page's own step in Build's place, e.g. the Levels page's plan step. */
  lastStep?: {
    title: string;
    description: ReactNode;
    footer: ReactNode;
    /** Like Build's `built`: Sync turns from optional to current. */
    built: boolean;
    done: boolean;
  };
  sync: {
    running: boolean;
    /** 0..100. */
    progress: number;
    /** Fresh, or locked until the next content cycle. */
    done: boolean;
    /** Completed in this visit: the solutions predate it. */
    syncedThisVisit: boolean;
    /** Formatted counts from the last sync. */
    collected?: { count: string; total: string };
    /** When the next sync opens, formatted; set while one per cycle is used up. */
    nextSyncAt?: string;
    onOpen: () => void;
  };
  /** The Gallery Planner, under the steps. */
  children?: ReactNode;
};

/**
 * Phones, once a club is imported: re-importing as a plain row over the
 * steps, with how old the import is, instead of a step card.
 */
const ImportRow = ({
  importAgeMs,
  onImportClub,
}: {
  /** 0 when the import time isn't known. */
  importAgeMs: number;
  onImportClub: () => void;
}) => {
  const { t } = useTranslation("gallery");
  return (
    <div className="flex items-center justify-between gap-x-3">
      <span className="min-w-0 truncate text-small text-neutral-400">
        {importAgeMs
          ? t("steps.import.lastImport", { time: formatTime(importAgeMs) })
          : t("club.imported")}
      </span>
      <Button variant="magic" size="small" onClick={onImportClub}>
        {t("club.import")}
      </Button>
    </div>
  );
};

// How far a swipe must travel (or how fast) to change card.
const SWIPE_PX = 50;
const FLICK_PX_PER_MS = 0.4;
// Movement before a touch picks an axis: sideways drags the cards, else the page scrolls.
const AXIS_LOCK_PX = 8;

/** The track's position: `index` cards along (each a full width plus the 1rem gap), then the drag. */
const trackTransform = (index: number, dragPx = 0) =>
  `translateX(calc(${-index} * (100% + 1rem) + ${dragPx}px))`;

/**
 * Phones: one step card at a time, on the first unfinished step. The cards sit
 * in a row, all as tall as the tallest, that follows a sideways drag and
 * snaps to the nearest. Dots under it say it swipes and jump to a card, so a
 * done or skipped step like Sync stays reachable. A new active step brings
 * the stack back to it.
 */
const PhoneStepStack = ({
  steps,
  active,
}: {
  steps: Step[];
  active: number;
}) => {
  const { t } = useTranslation("gallery");
  // The card picked by hand, by step, for the active step it was picked under:
  // a new active step, or that step leaving the list, drops it.
  const [picked, setPicked] = useState<{ key: string; activeKey: string }>();
  const pickedIndex = steps.findIndex((s) => s.key === picked?.key);
  const shown =
    picked?.activeKey === steps[active].key && pickedIndex !== -1
      ? pickedIndex
      : active;
  const show = (index: number) => {
    const step = steps[Math.max(0, Math.min(steps.length - 1, index))];
    setPicked({ key: step.key, activeKey: steps[active].key });
  };

  // The drag moves the track directly, so a touch move doesn't re-render the cards.
  const track = useRef<HTMLDivElement>(null);
  const gesture = useRef<{
    x: number;
    y: number;
    t: number;
    axis?: "x" | "y";
  }>(undefined);
  const drag = (dx: number) => {
    const el = track.current;
    if (!el) return;
    // Past the first and last card, a drag gives way to a third of the finger.
    const atEdge =
      (shown === 0 && dx > 0) || (shown === steps.length - 1 && dx < 0);
    el.style.transform = trackTransform(shown, atEdge ? dx / 3 : dx);
  };
  const release = () => {
    const el = track.current;
    if (!el) return;
    el.style.transition = "";
    el.style.transform = trackTransform(shown);
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div
        // Clipped at the screen's edges, so a card slides in from off screen.
        className="-mx-4 overflow-hidden px-4"
        style={{ touchAction: "pan-y" }}
        onTouchStart={(e) => {
          const p = e.touches[0];
          if (!p) return;
          gesture.current = { x: p.clientX, y: p.clientY, t: e.timeStamp };
        }}
        onTouchMove={(e) => {
          const g = gesture.current;
          const p = e.touches[0];
          if (!g || !p) return;
          const dx = p.clientX - g.x;
          const dy = p.clientY - g.y;
          if (!g.axis) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_PX) return;
            g.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
            // The card follows the finger, without easing behind it.
            if (g.axis === "x" && track.current)
              track.current.style.transition = "none";
          }
          if (g.axis === "x") drag(dx);
        }}
        onTouchEnd={(e) => {
          const g = gesture.current;
          gesture.current = undefined;
          if (!g || g.axis !== "x") return;
          const p = e.changedTouches[0];
          const dx = p ? p.clientX - g.x : 0;
          const speed = Math.abs(dx) / Math.max(1, e.timeStamp - g.t);
          release();
          if (Math.abs(dx) > SWIPE_PX || speed > FLICK_PX_PER_MS)
            show(shown + (dx < 0 ? 1 : -1));
        }}
        onTouchCancel={() => {
          gesture.current = undefined;
          release();
        }}
      >
        <div
          ref={track}
          className="flex gap-4 transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ transform: trackTransform(shown) }}
        >
          {steps.map((step, i) => (
            <div
              key={step.key}
              // Only the shown card takes focus and is read out.
              inert={i !== shown}
              aria-hidden={i !== shown}
              className="flex w-full shrink-0"
            >
              <StepCard
                step={step}
                stepOf={t("steps.stepOf", { n: i + 1, total: steps.length })}
              />
            </div>
          ))}
        </div>
      </div>
      {/* Says the cards swipe, and jumps to one. */}
      <div className="flex items-center justify-center gap-1.5">
        {steps.map((step, i) => (
          <button
            key={step.key}
            type="button"
            onClick={() => show(i)}
            aria-label={step.title}
            aria-current={i === shown ? "step" : undefined}
            className="flex h-6 items-center px-0.5"
          >
            <span
              className={cn(
                "h-1.5 rounded-full transition-all duration-200",
                i === shown ? "w-5 bg-neutral-200" : "w-1.5 bg-neutral-600",
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

/** The top of the Gallery: import, build and the optional collected-players sync. */
export const GallerySteps = ({
  clubImported,
  lastImportedAt,
  onImportClub,
  build,
  lastStep,
  sync,
  children,
}: GalleryStepsProps) => {
  const { t } = useTranslation("gallery");
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const [syncSkipped] = useState(
    () => safeGetItem(GALLERY_SYNC_SKIPPED_KEY) === "1",
  );
  const staleAfterSync = sync.syncedThisVisit && !!build && !build.done;

  const importAgeMs = galleryImportAge(lastImportedAt, Date.now());
  const { keys, states, active, importStale } = galleryStepStates({
    // Phones: once imported, re-importing is the ImportRow's, not a step.
    withImport: !isMobile || !clubImported,
    clubImported,
    importAgeMs,
    syncDone: sync.done,
    syncRunning: sync.running,
    syncSkipped,
    built: !!(build?.built ?? lastStep?.built),
    buildDone: !!(build?.done ?? lastStep?.done),
  });

  const importStep: Step = {
    key: "import",
    state: states.import,
    title: t("steps.import.title"),
    description: clubImported
      ? t("steps.import.descriptionDone")
      : t("steps.import.description"),
    footer:
      clubImported && !importStale ? (
        <CompletedChip size="medium">{t("club.imported")}</CompletedChip>
      ) : (
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5">
          <Button variant="magic" size="medium" onClick={onImportClub}>
            {t("club.import")}
          </Button>
          {importStale && (
            <span className="whitespace-nowrap text-small italic text-neutral-400 max-mobile:basis-full">
              {t("steps.import.lastImport", { time: formatTime(importAgeMs) })}
            </span>
          )}
        </span>
      ),
  };

  const syncStep: Step = {
    key: "sync",
    state: states.sync,
    // Neutral and square: an optional step is a fact, not a promo like Free.
    tag: (
      <span className="flex h-5 items-center whitespace-nowrap rounded-[5px] border border-neutral-700 bg-neutral-900 px-1.5 text-[11px] leading-none text-neutral-400">
        {t("steps.sync.optional")}
      </span>
    ),
    title: t("steps.sync.title"),
    description: sync.running
      ? t("steps.sync.syncing")
      : sync.done && sync.collected
        ? t("steps.sync.descriptionDone", sync.collected)
        : t("steps.sync.description"),
    footer: sync.done ? (
      <span className="flex items-center gap-2">
        <CompletedChip size="medium">{t("steps.sync.done")}</CompletedChip>
        {!sync.nextSyncAt && (
          <Tooltip asChild content={t("steps.sync.again")}>
            <button
              type="button"
              onClick={sync.onOpen}
              aria-label={t("steps.sync.again")}
              className="text-neutral-500 transition-colors hover:text-neutral-200"
            >
              <RefreshCw className="size-4" />
            </button>
          </Tooltip>
        )}
        <SyncInfo nextSyncAt={sync.nextSyncAt} />
      </span>
    ) : sync.running ? (
      <SyncProgress progress={sync.progress} />
    ) : (
      <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <SyncButton
          disabled={!clubImported}
          nextSyncAt={sync.nextSyncAt}
          onOpen={sync.onOpen}
        />
        <span className="flex items-center gap-1 whitespace-nowrap text-small text-neutral-500">
          <Clock className="size-3.5" />
          {t("steps.sync.duration")}
        </span>
      </span>
    ),
  };

  const buildStep: Step | undefined = build
    ? {
        key: "build",
        state: states.build,
        title: t("steps.build.title"),
        // A sync in this visit makes the solutions worth updating; it says so in
        // the description's place, so the card doesn't grow.
        description: build.done ? (
          t("steps.build.doneDescription")
        ) : staleAfterSync && !build.running ? (
          <span className="text-streamline-points">
            <Check className="mr-1.5 inline size-3.5 align-[-2px] text-complete" />
            {t("steps.build.syncedNote")}
          </span>
        ) : (
          t("steps.build.description")
        ),
        footer: build.done ? (
          <CompletedChip size="medium">{t("steps.build.done")}</CompletedChip>
        ) : (
          // One Button for idle and running, so its loading clusters start where it was pressed.
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5">
            <Button
              variant="credit-magic"
              size="medium"
              disabled={build.disabled}
              loading={build.running}
              onClick={build.onBuild}
            >
              {build.running ? (
                <span className="animate-pulse tabular-nums text-holo">
                  {t(
                    build.built
                      ? "steps.build.updating"
                      : "steps.build.building",
                    build.progress,
                  )}
                </span>
              ) : (
                <span className={cn(!build.disabled && "text-holo")}>
                  {build.built
                    ? t("steps.build.rebuild")
                    : t("steps.build.cta")}
                </span>
              )}
            </Button>
            <CreditCostMarker
              featureId="gallery-solutions-built"
              when={build.costsCredit && !build.running}
            />
            {!build.running && build.built && build.newCollectedCount > 0 && (
              <span className="whitespace-nowrap text-small italic text-neutral-400 max-mobile:basis-full">
                {t("steps.build.newCollected", {
                  count: build.newCollectedCount,
                })}
              </span>
            )}
          </span>
        ),
      }
    : lastStep && {
        key: "build",
        state: states.build,
        title: lastStep.title,
        description: lastStep.description,
        footer: lastStep.footer,
      };

  const steps = keys.flatMap((key) => {
    const step = { import: importStep, sync: syncStep, build: buildStep }[key];
    return step ? [step] : [];
  });

  if (isMobile)
    return (
      <section className="flex flex-col gap-3.5">
        {clubImported && (
          <ImportRow importAgeMs={importAgeMs} onImportClub={onImportClub} />
        )}
        <PhoneStepStack
          steps={steps}
          active={Math.min(active, steps.length - 1)}
        />
        {children}
      </section>
    );

  return (
    <section className="flex flex-col gap-3.5">
      <div className="flex flex-row gap-2.5">
        {steps.map((step, i) => (
          <Fragment key={step.key}>
            {i > 0 && (
              <ArrowRight
                strokeWidth={2.5}
                className="size-5 shrink-0 self-center text-white"
              />
            )}
            <StepCard step={step} />
          </Fragment>
        ))}
      </div>
      {children}
    </section>
  );
};
